import { loadStripe } from '@stripe/stripe-js';
import { supabase } from '../lib/supabase';

// Initialize Stripe with live publishable key
let stripePromise = null;

const getStripe = () => {
  if (!stripePromise) {
    const publishableKey = import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY;
    
    if (!publishableKey) {
      console.error('Stripe publishable key not configured');
      return null;
    }
    
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
};

// Credit packages with appealing pricing
export const CREDIT_PACKAGES = [
  {
    id: 'starter',
    name: 'Starter Pack',
    credits: 25,
    price: 999, // $9.99
    priceDisplay: '$9.99',
    perCreditCost: '$0.40',
    discount: null,
    popular: false,
    features: [
      '25 AI Recommendations',
      'Valid for 90 days',
      'Priority Support'
    ]
  },
  {
    id: 'value',
    name: 'Value Pack',
    credits: 100,
    price: 2999, // $29.99
    priceDisplay: '$29.99',
    perCreditCost: '$0.30',
    discount: '25% OFF',
    popular: true,
    savings: '$10.00',
    features: [
      '100 AI Recommendations',
      'Valid for 180 days',
      'Priority Support',
      'Save $10 vs Starter'
    ]
  },
  {
    id: 'professional',
    name: 'Professional Pack',
    credits: 250,
    price: 5999, // $59.99
    priceDisplay: '$59.99',
    perCreditCost: '$0.24',
    discount: '40% OFF',
    popular: false,
    savings: '$40.00',
    features: [
      '250 AI Recommendations',
      'Valid for 365 days',
      'Priority Support',
      'Dedicated Account Manager',
      'Save $40 vs Starter'
    ]
  },
  {
    id: 'enterprise',
    name: 'Enterprise Pack',
    credits: 500,
    price: 9999, // $99.99
    priceDisplay: '$99.99',
    perCreditCost: '$0.20',
    discount: '50% OFF',
    popular: false,
    savings: '$100.00',
    features: [
      '500 AI Recommendations',
      'Never Expires',
      'Priority Support',
      'Dedicated Account Manager',
      'Custom Integrations',
      'Save $100 vs Starter'
    ]
  }
];

/**
 * Create a Stripe Checkout Session for credit purchase
 */
export const createCreditPurchaseSession = async (packageId, userId) => {
  try {
    const selectedPackage = CREDIT_PACKAGES?.find(pkg => pkg?.id === packageId);
    
    if (!selectedPackage) {
      throw new Error('Invalid package selected');
    }

    // Get user profile for customer info
    const { data: profile, error: profileError } = await supabase
      ?.from('user_profiles')
      ?.select('*')
      ?.eq('id', userId)
      ?.single();

    if (profileError) throw profileError;

    // Create payment intent via Supabase Edge Function
    const { data, error } = await supabase?.functions?.invoke('create-payment-intent', {
      body: {
        packageId: packageId,
        userId: userId,
        amount: selectedPackage?.price,
        credits: selectedPackage?.credits,
        packageName: selectedPackage?.name,
        customerEmail: profile?.email,
        customerName: profile?.full_name
      }
    });

    if (error) {
      console.error('Payment intent creation error:', error);
      throw new Error(error?.message || 'Failed to create payment session');
    }

    if (!data?.clientSecret) {
      throw new Error('Payment session creation failed - no client secret received');
    }

    return {
      success: true,
      clientSecret: data?.clientSecret,
      purchaseId: data?.purchaseId,
      package: selectedPackage
    };
  } catch (error) {
    console.error('Error creating purchase session:', error);
    return {
      success: false,
      error: error?.message
    };
  }
};

/**
 * Confirm payment and update credits
 */
export const confirmCreditPurchase = async (paymentIntentId) => {
  try {
    const { data, error } = await supabase?.functions?.invoke('confirm-payment', {
      body: { paymentIntentId }
    });

    if (error) throw error;

    return {
      success: true,
      creditsAdded: data?.creditsAdded,
      newBalance: data?.newBalance
    };
  } catch (error) {
    console.error('Error confirming purchase:', error);
    return {
      success: false,
      error: error?.message
    };
  }
};

/**
 * Complete a credit purchase (called after successful Stripe payment)
 */
export const completeCreditPurchase = async (purchaseId, paymentIntentId) => {
  try {
    const { data, error } = await supabase?.rpc('complete_credit_purchase', {
      p_purchase_id: purchaseId,
      p_stripe_payment_intent_id: paymentIntentId
    });

    if (error) throw error;

    return {
      success: data?.success || false,
      creditsAdded: data?.credits_added,
      error: data?.error
    };
  } catch (error) {
    console.error('Error completing purchase:', error);
    return {
      success: false,
      error: error?.message
    };
  }
};

/**
 * Get user's purchase history
 */
export const getUserPurchaseHistory = async (userId) => {
  try {
    const { data, error } = await supabase
      ?.from('credit_purchases')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    return {
      success: true,
      purchases: data || []
    };
  } catch (error) {
    console.error('Error fetching purchase history:', error);
    return {
      success: false,
      error: error?.message,
      purchases: []
    };
  }
};

/**
 * Calculate potential savings
 */
export const calculateSavings = (currentCredits, targetPackage) => {
  const starterPack = CREDIT_PACKAGES?.find(pkg => pkg?.id === 'starter');
  if (!starterPack) return 0;

  const starterCostPerCredit = starterPack?.price / starterPack?.credits;
  const targetCostPerCredit = targetPackage?.price / targetPackage?.credits;
  
  return Math.round((starterCostPerCredit - targetCostPerCredit) * targetPackage?.credits);
};

/**
 * Format amount for display
 */
export const formatAmount = (amountCents) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  })?.format(amountCents / 100);
};

export default {
  getStripe,
  CREDIT_PACKAGES,
  createCreditPurchaseSession,
  confirmCreditPurchase,
  completeCreditPurchase,
  getUserPurchaseHistory,
  calculateSavings,
  formatAmount
};